<template>
    <div class="main-content">
        <!-- TOASTS DE NOTIFICAÇÃO -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">{{ alert.message }}</div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0 flex-nowrap text-nowrap align-items-center">
                    <li class="breadcrumb-item"><router-link class="nav-link d-inline" to="/dashboard">Início</router-link></li>
                    <li class="breadcrumb-item"><router-link class="nav-link d-inline" to="/dashboard/page">Landing
                            Pages</router-link></li>
                    <li class="breadcrumb-item active text-dark small" aria-current="page">
                        {{ isEditing ? 'Editar Página' : 'Nova Página' }}
                    </li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom flex-wrap gap-3">
                <div>
                    <h4 class="mb-0 fw-bold">Nova Página</h4>
                    <span class="text-muted fs-7">Construa páginas incríveis com blocos modulares</span>
                </div>

                <div class="d-flex gap-2 flex-wrap">
                    <button class="btn btn-outline-primary rounded-pill px-3 shadow-sm d-flex align-items-center"
                        @click="openFullPreviewModal">
                        <i class="bi bi-display me-2"></i> Ver no Navegador
                    </button>
                    <button
                        class="btn btn-pmg-light rounded-pill px-3 shadow-sm d-flex align-items-center border-warning text-dark"
                        @click="openAiModal">
                        <i class="bi bi-magic me-2 text-warning"></i> Criar com IA
                    </button>
                    <button class="btn btn-pmg-primary" @click="savePage" :disabled="loading">
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <i v-else class="bi bi-floppy me-1"></i>
                        {{ loading ? 'Salvando...' : (isEditing ? 'Atualizar Página' : 'Salvar Página') }}
                    </button>
                </div>
            </div>

            <div class="row g-4">
                <!-- COLUNA ESQUERDA: EDITOR -->
                <div class="col-lg-7">

                    <!-- CONFIGURAÇÃO INICIAL -->
                    <div class="card bg-light border-0 rounded-4 p-3 mb-4">
                        <h6 class="fw-bold mb-3 text-secondary">Configuração Inicial</h6>
                        <div class="row g-3">
                            <div class="col-md-6">
                                <div class="d-flex justify-content-between align-items-end mb-1">
                                    <label class="form-label small fw-bold text-muted mb-0">Título da Página</label>
                                    <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                        @click="form.show_title = !form.show_title">
                                        <i class="bi"
                                            :class="form.show_title !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                    </button>
                                </div>
                                <input v-model="form.title" type="text" class="form-control form-control-pmg"
                                    placeholder="Título..." :class="{ 'opacity-50': form.show_title === false }">
                            </div>
                            <div class="col-md-6">
                                <label class="form-label-modern mb-1">Template Inicial</label>
                                <select v-model="form.template" @change="handleTemplateChange"
                                    class="form-select form-control-pmg-modern">
                                    <option value="">Página em Branco</option>
                                    <option value="layout_1">Layout Moderno (1)</option>
                                    <option value="layout_2">Layout Clean (2)</option>
                                    <option value="layout_3">Campanha de Arrecadação</option>
                                </select>
                            </div>
                            <div class="col-md-6">
                                <label class="form-label small fw-bold text-muted mb-1">Cor do Título da Página</label>
                                <div class="input-group input-group-sm">
                                    <input v-model="form.title_color" type="color"
                                        class="form-control form-control-color" style="width: 45px;">
                                    <input v-model="form.title_color" type="text" class="form-control"
                                        placeholder="#5F0487">
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- LOOP DE SEÇÕES (CONSTRUTOR) -->
                    <div class="card bg-light border-0 rounded-4 p-3 mb-4">
                        <div class="d-flex justify-content-between align-items-center mb-3">
                            <h6 class="fw-bold m-0 text-secondary">Conteúdo da Página (Blocos)</h6>
                        </div>

                        <div v-for="(section, index) in form.sections" :key="index"
                            class="card bg-white border rounded-4 p-3 mb-4 shadow-sm position-relative">

                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <div class="d-flex align-items-center gap-2">
                                    <span class="badge rounded-pill px-3 py-2 text-uppercase"
                                        style="background-color: #5F0487; color: white;">
                                        <i class="bi bi-grid-3x3-gap-fill me-1"></i> {{ section.type }}
                                    </span>
                                </div>
                                <div class="btn-group shadow-sm">
                                    <button class="btn btn-sm btn-light border" @click="moveSection(index, -1)"
                                        :disabled="index === 0"><i class="bi bi-arrow-up"></i></button>
                                    <button class="btn btn-sm btn-light border" @click="moveSection(index, 1)"
                                        :disabled="index === form.sections.length - 1"><i
                                            class="bi bi-arrow-down"></i></button>
                                    <button class="btn btn-sm btn-outline-danger" @click="removeSection(index)"><i
                                            class="bi bi-trash"></i></button>
                                </div>
                            </div>

                            <!-- CONTROLES DE CORES GERAIS -->
                            <div v-if="section.type !== 'dailypag_crowdfunding'"
                                class="row g-3 mb-3 bg-light p-2 rounded-3 align-items-end">
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted mb-1">Cor de Fundo</label>
                                    <div class="input-group input-group-sm">
                                        <input v-model="section.content.bg_color" type="color"
                                            class="form-control form-control-color" style="width: 45px;">
                                        <input v-model="section.content.bg_color" type="text" class="form-control">
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label small fw-bold text-muted mb-1">Cor de Destaque /
                                        Título</label>
                                    <div class="input-group input-group-sm">
                                        <input v-model="section.content.accent_color" type="color"
                                            class="form-control form-control-color" style="width: 45px;">
                                        <input v-model="section.content.accent_color" type="text" class="form-control">
                                    </div>
                                </div>
                            </div>

                            <!-- 1. CAMPANHA DE ARRECADAÇÃO RMR -->
                            <div v-if="section.type === 'dailypag_crowdfunding'" class="section-body">
                                <!-- 1. INFORMAÇÕES PRINCIPAIS -->
                                <h6 class="fw-bold border-bottom pb-2 mb-3" style="color: #5F0487;">1. Informações
                                    Principais</h6>
                                <div class="row g-3 mb-4">
                                    <div class="col-md-12">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Título da Campanha</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_title = !section.content.show_title">
                                                <i class="bi"
                                                    :class="section.content.show_title !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input v-model="section.content.title" type="text"
                                            class="form-control form-control-pmg-modern"
                                            :class="{ 'opacity-50': section.content.show_title === false }">
                                    </div>

                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Categoria</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_category = !section.content.show_category">
                                                <i class="bi"
                                                    :class="section.content.show_category !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <SearchableSelect v-model="section.content.category" :options="categories"
                                            placeholder="SELECIONE UMA CATEGORIA..."
                                            :disabled="section.content.show_category === false" />
                                    </div>

                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Localização</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_location = !section.content.show_location">
                                                <i class="bi"
                                                    :class="section.content.show_location !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input v-model="section.content.location" type="text"
                                            class="form-control form-control-pmg-modern text-uppercase"
                                            :class="{ 'opacity-50': section.content.show_location === false }">
                                    </div>

                                    <div class="col-md-12 mt-2">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Imagem de Capa (Banner)</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_cover_image = !section.content.show_cover_image">
                                                <i class="bi"
                                                    :class="section.content.show_cover_image !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <div :class="{ 'opacity-50': section.content.show_cover_image === false }">
                                            <ImageCropper :aspectRatio="16 / 9"
                                                @cropped="blob => handleCroppedBlob(blob, section.content, 'cover_image')" />
                                        </div>
                                    </div>
                                </div>

                                <!-- 2. METAS E ESTATÍSTICAS -->
                                <h6 class="fw-bold border-bottom pb-2 mb-3 mt-4" style="color: #5F0487;">2. Metas e
                                    Estatísticas</h6>
                                <div class="row g-3 mb-4">
                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Valor Arrecadado (R$)</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_raised_amount = !section.content.show_raised_amount">
                                                <i class="bi"
                                                    :class="section.content.show_raised_amount !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input :value="formatCurrency(section.content.raised_amount)" type="text"
                                            class="form-control form-control-pmg-modern fw-bold text-success"
                                            :class="{ 'opacity-50': section.content.show_raised_amount === false }"
                                            disabled>
                                    </div>

                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Meta Total (R$)</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_goal = !section.content.show_goal">
                                                <i class="bi"
                                                    :class="section.content.show_goal !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input :value="formatCurrency(section.content.goal_amount)"
                                            @input="handleCurrencyInput($event, section.content, 'goal_amount')"
                                            type="text" class="form-control form-control-pmg-modern fw-bold"
                                            :class="{ 'opacity-50': section.content.show_goal === false }">
                                    </div>

                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Doadores (Apoios)</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_supporters = !section.content.show_supporters">
                                                <i class="bi"
                                                    :class="section.content.show_supporters !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input v-model.number="section.content.supporters_count" type="number"
                                            class="form-control form-control-pmg-modern"
                                            :class="{ 'opacity-50': section.content.show_supporters === false }"
                                            disabled>
                                    </div>

                                    <!-- CONTROLE DO BOTÃO DE COMPARTILHAR -->
                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <label class="form-label-modern mb-0">Botão de Compartilhar</label>
                                            <button type="button"
                                                class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                @click="section.content.show_share_button = section.content.show_share_button === false ? true : false">
                                                <i class="bi"
                                                    :class="section.content.show_share_button !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <div class="form-control-pmg-modern bg-light text-muted d-flex align-items-center justify-content-between"
                                            :class="{ 'opacity-50': section.content.show_share_button === false }">
                                            <span class="small">Exibir botão de compartilhamento</span>
                                            <i class="bi bi-share"></i>
                                        </div>
                                    </div>

                                    <!-- PERSONALIZAÇÃO DO BOTÃO DE DOAÇÃO -->
                                    <div class="col-md-8">
                                        <label class="form-label-modern mb-1">Texto do Botão Principal</label>
                                        <input v-model="section.content.button_text" type="text"
                                            class="form-control form-control-pmg-modern"
                                            placeholder="Ex: Quero Ajudar Agora">
                                    </div>
                                    <div class="col-md-4">
                                        <label class="form-label-modern mb-1">Cor do Botão</label>
                                        <div class="input-group">
                                            <input v-model="section.content.button_color" type="color"
                                                class="form-control form-control-color"
                                                style="width: 45px; height: 46px; border-radius: 12px 0 0 12px !important;">
                                            <input v-model="section.content.button_color" type="text"
                                                class="form-control form-control-pmg-modern"
                                                style="border-radius: 0 12px 12px 0 !important;" placeholder="#5F0487">
                                        </div>
                                    </div>
                                </div>

                                <!-- 3. SOBRE A CAMPANHA -->
                                <h6 class="fw-bold border-bottom pb-2 mb-3 mt-4" style="color: #5F0487;">3. Sobre a
                                    Campanha</h6>

                                <!-- RESUMO BREVE -->
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <label class="form-label-modern mb-0">Resumo Breve</label>
                                        <button type="button"
                                            class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                            @click="section.content.show_short_description = !section.content.show_short_description">
                                            <i class="bi"
                                                :class="section.content.show_short_description !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <div :class="{ 'opacity-50': section.content.show_short_description === false }">
                                        <div class="editor-wrapper">
                                            <div class="editor-toolbar">
                                                <button type="button" class="btn-editor-action"
                                                    @click="formatText('bold')" title="Negrito">
                                                    <i class="bi bi-type-bold"></i>
                                                </button>
                                                <button type="button" class="btn-editor-action"
                                                    @click="formatText('italic')" title="Itálico">
                                                    <i class="bi bi-type-italic"></i>
                                                </button>
                                            </div>
                                            <div contenteditable="true" class="editor-content" style="min-height: 80px;"
                                                @blur="e => section.content.short_description = e.target.innerHTML"
                                                v-html="section.content.short_description"></div>
                                        </div>
                                    </div>
                                </div>

                                <!-- CHAVE PIX -->
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <label class="form-label-modern mb-0">Chave PIX da Campanha</label>
                                        <button type="button"
                                            class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                            @click="section.content.show_pix_key = !section.content.show_pix_key"
                                            title="Exibir/Ocultar na página">
                                            <i class="bi"
                                                :class="section.content.show_pix_key !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <input type="text" class="form-control form-control-pmg-modern bg-light text-muted"
                                        placeholder="Gerada automaticamente pelo sistema" disabled
                                        :class="{ 'opacity-50': section.content.show_pix_key === false }">
                                    <small class="text-muted d-block mt-1.5" style="font-size: 0.78rem;">
                                        <i class="bi bi-info-circle me-1"></i> Uma chave PIX única será gerada
                                        automaticamente para esta campanha.
                                    </small>
                                </div>

                                <!-- HISTÓRIA COMPLETA (ABA SOBRE) -->
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-center mb-1">
                                        <label class="form-label-modern mb-0">História Completa (Aba Sobre)</label>
                                        <button type="button"
                                            class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                            @click="section.content.show_about_text = !section.content.show_about_text">
                                            <i class="bi"
                                                :class="section.content.show_about_text !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <div :class="{ 'opacity-50': section.content.show_about_text === false }">
                                        <div class="editor-wrapper">
                                            <div class="editor-toolbar">
                                                <button type="button" class="btn-editor-action"
                                                    @click="formatText('bold')" title="Negrito">
                                                    <i class="bi bi-type-bold"></i>
                                                </button>
                                                <button type="button" class="btn-editor-action"
                                                    @click="formatText('italic')" title="Itálico">
                                                    <i class="bi bi-type-italic"></i>
                                                </button>
                                            </div>
                                            <div contenteditable="true" class="editor-content"
                                                style="min-height: 130px;"
                                                @blur="e => section.content.about_text = e.target.innerHTML"
                                                v-html="section.content.about_text"></div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 4. CONFIGURAÇÃO DE DOAÇÃO -->
                                <h6 class="fw-bold border-bottom pb-2 mb-3 mt-4" style="color: #5F0487;">4. Configuração
                                    de Doação</h6>
                                <div class="p-3 bg-light border rounded-4 mb-3">
                                    <div class="row g-3">
                                        <div class="col-md-6">
                                            <div class="d-flex justify-content-between align-items-center mb-1">
                                                <label class="form-label-modern mb-0">Nome Organizador</label>
                                                <button type="button"
                                                    class="btn btn-sm btn-link p-0 text-decoration-none btn-eye-toggle"
                                                    @click="section.content.show_creator = !section.content.show_creator">
                                                    <i class="bi"
                                                        :class="section.content.show_creator !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                                </button>
                                            </div>
                                            <input v-model="section.content.creator_name" type="text"
                                                class="form-control form-control-pmg-modern"
                                                :class="{ 'opacity-50': section.content.show_creator === false }">
                                        </div>

                                        <div class="col-md-6">
                                            <label class="form-label-modern mb-1">Valor Sugerido/Base (R$)</label>
                                            <input :value="formatCurrency(section.content.amount)"
                                                @input="handleCurrencyInput($event, section.content, 'amount')"
                                                @blur="validateMinAmount(section.content)" type="text"
                                                class="form-control form-control-pmg-modern" placeholder="10,00">
                                        </div>

                                        <div class="col-12">
                                            <div class="form-check form-switch mt-2">
                                                <input class="form-check-input" type="checkbox"
                                                    v-model="section.content.allows_fractional" :id="'frac_' + index">
                                                <label class="form-check-label small fw-bold text-dark ms-1"
                                                    :for="'frac_' + index">
                                                    Permitir doador escolher o valor que deseja doar?
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- DEMAIS BLOCOS... -->
                            <div v-else-if="section.type === 'text'">
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-end mb-1">
                                        <label class="form-label small fw-bold text-muted mb-0">Título do Bloco</label>
                                        <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                            @click="section.content.show_title = !section.content.show_title">
                                            <i class="bi"
                                                :class="section.content.show_title !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <input v-model="section.content.title" type="text"
                                        class="form-control form-control-pmg-modern"
                                        :class="{ 'opacity-50': section.content.show_title === false }">
                                </div>
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-end mb-1">
                                        <label class="form-label small fw-bold text-muted mb-0">Conteúdo do
                                            Texto</label>
                                        <button type="button"
                                            class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                            @click="section.content.show_text = !section.content.show_text">
                                            <i class="bi"
                                                :class="section.content.show_text !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>

                                    <!-- EDITOR COM BARRA DE FERRAMENTAS -->
                                    <div :class="{ 'opacity-50': section.content.show_text === false }">
                                        <div class="editor-wrapper">
                                            <div class="editor-toolbar">
                                                <button type="button" class="btn-editor-action"
                                                    @click="formatText('bold')" title="Negrito">
                                                    <i class="bi bi-type-bold"></i>
                                                </button>
                                                <button type="button" class="btn-editor-action"
                                                    @click="formatText('italic')" title="Itálico">
                                                    <i class="bi bi-type-italic"></i>
                                                </button>
                                            </div>
                                            <div contenteditable="true" class="editor-content"
                                                style="min-height: 100px;"
                                                @blur="e => section.content.text = e.target.innerHTML"
                                                v-html="section.content.text"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="section.type === 'youtube'">
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-end mb-1">
                                        <label class="form-label small fw-bold text-muted mb-0">Título do Vídeo</label>
                                        <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                            @click="section.content.show_title = !section.content.show_title">
                                            <i class="bi"
                                                :class="section.content.show_title !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <input v-model="section.content.title" type="text"
                                        class="form-control form-control-pmg-modern"
                                        :class="{ 'opacity-50': section.content.show_title === false }">
                                </div>
                                <div class="mb-2">
                                    <div class="d-flex justify-content-between align-items-end mb-1">
                                        <label class="form-label small fw-bold text-muted mb-0">URL do Vídeo</label>
                                        <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                            @click="section.content.show_url = !section.content.show_url">
                                            <i class="bi"
                                                :class="section.content.show_url !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <input v-model="section.content.url" type="text"
                                        class="form-control form-control-pmg"
                                        :class="{ 'opacity-50': section.content.show_url === false }">
                                </div>
                            </div>

                            <div v-else-if="section.type === 'contact'">
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-end mb-1">
                                        <label class="form-label small fw-bold text-muted mb-0">Título da Seção de
                                            Contato</label>
                                        <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                            @click="section.content.show_title = !section.content.show_title">
                                            <i class="bi"
                                                :class="section.content.show_title !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <input v-model="section.content.title" type="text"
                                        class="form-control form-control-pmg-modern" placeholder="Fale Conosco"
                                        :class="{ 'opacity-50': section.content.show_title === false }">
                                </div>
                                <div class="row g-2">
                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-end mb-1">
                                            <label class="form-label small text-muted mb-0">WhatsApp</label>
                                            <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                                @click="section.content.show_whatsapp = !section.content.show_whatsapp">
                                                <i class="bi"
                                                    :class="section.content.show_whatsapp !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input v-model="section.content.whatsapp" type="text"
                                            class="form-control form-control-pmg-modern" placeholder="(11) 90000-0000"
                                            :class="{ 'opacity-50': section.content.show_whatsapp === false }">
                                    </div>
                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-end mb-1">
                                            <label class="form-label small text-muted mb-0">E-mail</label>
                                            <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                                @click="section.content.show_email = !section.content.show_email">
                                                <i class="bi"
                                                    :class="section.content.show_email !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input v-model="section.content.email" type="text"
                                            class="form-control form-control-pmg-modern"
                                            placeholder="contato@dailypag.com.br"
                                            :class="{ 'opacity-50': section.content.show_email === false }">
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="section.type === 'payment'">
                                <div class="row g-3">
                                    <!-- NOME DO BOTÃO -->
                                    <div class="col-md-6">
                                        <div class="d-flex justify-content-between align-items-end mb-1">
                                            <label class="form-label small fw-bold text-muted mb-0">Texto do
                                                Botão</label>
                                            <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                                @click="section.content.show_button_text = !section.content.show_button_text">
                                                <i class="bi"
                                                    :class="section.content.show_button_text !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                            </button>
                                        </div>
                                        <input v-model="section.content.button_text" type="text"
                                            class="form-control form-control-pmg-modern"
                                            placeholder="Ex: Garantir Acesso Imediato"
                                            :class="{ 'opacity-50': section.content.show_button_text === false }">
                                    </div>

                                    <!-- TIPO DE AÇÃO -->
                                    <div class="col-md-6">
                                        <label class="form-label-modern mb-1">Ação do Botão</label>
                                        <select v-model="section.content.button_action"
                                            class="form-select form-control-pmg-modern">
                                            <option value="checkout">Abrir Checkout (Pagamento)</option>
                                            <option value="link">Redirecionar para URL (Link Externo)</option>
                                        </select>
                                    </div>

                                    <!-- CAMPO DE VALOR (Aparece só se for Checkout) -->
                                    <div v-if="section.content.button_action !== 'link'" class="col-md-12 mt-2">
                                        <label class="form-label-modern mb-1">Valor Base (R$)</label>
                                        <input :value="formatCurrency(section.content.amount)"
                                            @input="handleCurrencyInput($event, section.content, 'amount')" type="text"
                                            class="form-control form-control-pmg-modern" placeholder="0,00">
                                    </div>

                                    <!-- CAMPO DE URL (Aparece só se for Link Externo) -->
                                    <div v-if="section.content.button_action === 'link'" class="col-md-12 mt-2">
                                        <label class="form-label-modern mb-1">URL de Redirecionamento</label>
                                        <input v-model="section.content.button_link" type="url"
                                            class="form-control form-control-pmg-modern" placeholder="https://...">
                                    </div>
                                </div>
                            </div>

                            <div v-else-if="section.type === 'carousel'">
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between align-items-end mb-1">
                                        <label class="form-label small fw-bold text-muted mb-0">Título da
                                            Galeria</label>
                                        <button class="btn btn-sm btn-link p-0 text-decoration-none shadow-none"
                                            @click="section.content.show_title = !section.content.show_title">
                                            <i class="bi"
                                                :class="section.content.show_title !== false ? 'bi-eye text-primary' : 'bi-eye-slash text-danger'"></i>
                                        </button>
                                    </div>
                                    <input v-model="section.content.title" type="text"
                                        class="form-control form-control-pmg" placeholder="Título da galeria"
                                        :class="{ 'opacity-50': section.content.show_title === false }">
                                </div>

                                <div v-for="(img, imgIdx) in section.content.images" :key="imgIdx"
                                    class="mb-3 p-3 border rounded-3 bg-white shadow-sm">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <label class="form-label small fw-bold text-muted mb-0">Imagem #{{ imgIdx + 1
                                            }}</label>
                                        <button class="btn btn-outline-danger btn-sm border-0"
                                            @click="removeCarouselImage(section, imgIdx)" title="Remover imagem">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </div>
                                    <div v-if="img.url" class="mb-3 position-relative d-inline-block">
                                        <img :src="img.url" class="img-thumbnail rounded"
                                            style="max-height: 150px; object-fit: cover;">
                                    </div>
                                    <!-- Passa explicitamente o 'img' específico deste loop -->
                                    <ImageCropper :aspectRatio="16 / 9"
                                        @cropped="blob => handleCroppedBlob(blob, img, 'url')" />
                                </div>
                                <button class="btn btn-sm btn-outline-secondary w-100 py-2 border-dashed"
                                    @click="section.content.images.push({ url: '', id: null })">
                                    <i class="bi bi-plus-lg me-1"></i> Adicionar Imagem
                                </button>
                            </div>

                        </div>

                        <!-- ADICIONAR NOVA SEÇÃO -->
                        <!-- ADICIONAR NOVA SEÇÃO (SEM O BOTÃO RMR DUPLICADO) -->
                        <div class="text-center mt-3 p-3 border border-dashed rounded-3 bg-white"
                            style="border-style: dashed !important;">
                            <span class="d-block small text-muted mb-3">Adicionar novo bloco à página</span>
                            <div class="d-flex flex-wrap justify-content-center gap-2">
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('text')">
                                    <i class="bi bi-text-paragraph"></i> Texto
                                </button>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('youtube')">
                                    <i class="bi bi-youtube text-danger"></i> Vídeo
                                </button>
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('contact')">
                                    <i class="bi bi-telephone"></i> Contato
                                </button>
                                <!-- <button class="btn btn-sm btn-pmg-light" @click="addSection('payment')">
                                    <i class="bi bi-credit-card text-success"></i> Pagamento
                                </button> -->
                                <button class="btn btn-sm btn-pmg-light" @click="addSection('carousel')">
                                    <i class="bi bi-images text-info"></i> Carrossel
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- COLUNA DIREITA: LIVE PREVIEW INTERNO -->
                <div class="col-lg-5">
                    <div class="preview-container shadow-lg rounded-4 p-0 sticky-top bg-white d-flex flex-column overflow-hidden modern-preview-font"
                        style="top: 20px; height: 85vh; border: 8px solid #f1f5f9; background: #f8f9fa;">
                        <PagePreviewRenderer :form="form" @openCheckout="openCheckoutModal" />
                    </div>
                </div>
            </div>
        </div>

        <!-- MODAL TELA CHEIA (VER NO NAVEGADOR) -->
        <div v-if="fullPreviewModalOpen" class="modal-browser-overlay" @click.self="closeFullPreviewModal">
            <div class="modal-browser-container shadow-lg">
                <div
                    class="browser-top-bar d-flex align-items-center justify-content-between px-3 py-2 bg-dark text-white">
                    <div class="d-flex align-items-center gap-2">
                        <span class="dot-red"></span>
                        <span class="dot-yellow"></span>
                        <span class="dot-green"></span>
                        <span
                            class="ms-3 small text-muted font-monospace bg-secondary bg-opacity-25 px-3 py-1 rounded-pill">https://dailypag.com.br/{{
                                form.title ? form.title.toLowerCase().replace(/ /g, '-') : 'pagina' }}</span>
                    </div>
                    <button class="btn btn-sm btn-danger" @click="closeFullPreviewModal" title="Fechar">
                        <i class="bi bi-x-lg"></i> Fechar
                    </button>
                </div>
                <div class="browser-body flex-grow-1 bg-white modern-preview-font overflow-y-auto">
                    <PagePreviewRenderer :form="form" @openCheckout="openCheckoutModal" />
                </div>
            </div>
        </div>

        <!-- MODAL DA IA -->
        <transition name="fade">
    <div v-if="showAiModal" class="ai-modal-overlay" @click.self="closeAiModal">
        <div class="ai-modal-content shadow-lg rounded-4 bg-white p-4">
            
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="fw-bold mb-0 d-flex align-items-center" style="color: #5F0487;">
                    <i class="bi bi-magic fs-4 me-2 text-warning"></i> Assistente IA
                </h5>
                <button class="btn-close" @click="closeAiModal" :disabled="aiLoading"></button>
            </div>
            
            <p class="text-muted small mb-4">
                Descreva a causa ou o objetivo da sua campanha e nossa IA montará a página completa para você.
            </p>
            
            <div class="mb-4">
                <textarea v-model="aiObjective" class="form-control" rows="4"
                    placeholder="Ex: Arrecadar fundos para a ONG de animais abandonados da cidade..." 
                    :disabled="aiLoading"></textarea>
            </div>
            
            <div class="d-flex justify-content-end gap-2">
                <button class="btn btn-light border" @click="closeAiModal" :disabled="aiLoading">
                    Cancelar
                </button>
                
               <button class="btn text-white fw-bold px-4 rounded-pill shadow-sm transition-all btn btn-pmg-primary" 
    style="background-color: #5F0487;" 
    @click="generateWithAi"
    :disabled="aiLoading || aiObjective.length < 5">
    
    <!-- ESTADO 1: CARREGANDO (Mostra o spinner e o texto "Criando Magia...") -->
    <span v-if="aiLoading" class="d-inline-flex align-items-center">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Criando Magia...
    </span>
    
    <!-- ESTADO 2: NORMAL (Mostra o ícone do robô e o texto "Gerar Campanha") -->
    <span v-else class="d-inline-flex align-items-center">
        <i class="bi bi-robot fs-5 me-2"></i>
        Gerar Campanha
    </span>
    
</button>
            </div>
            
        </div>
    </div>
</transition>

        <!-- MODAL DE CHECKOUT REAL (Reutilizado) -->
        <DonationModal v-if="checkoutModalOpen" :campaign="activeCheckoutContent" :is-preview="true"
            @close="closeCheckoutModal" />

    </div>
</template>
<script>
import ImageCropper from '@/components/ImageCropper.vue';
import PagePreviewRenderer from './PagePreviewRenderer.vue';
import SearchableSelect from '@/components/SearchableSelect.vue';
import DonationModal from '@/components/DonationModal.vue';
import api from '@/services/api';

export default {
    components: {
        ImageCropper,
        PagePreviewRenderer,
        SearchableSelect,
        DonationModal
    },
    data() {
        return {
            loading: false,
            isEditing: false,
            pageId: null,
            alerts: [],
            fullPreviewModalOpen: false,
            showAiModal: false,
            aiObjective: '',
            aiLoading: false,

            checkoutModalOpen: false,
            activeCheckoutContent: {},
            displayCheckoutAmount: '',
            checkoutAmountRaw: 0,
            pendingFiles: [],

            form: {
                title: 'Nova Página',
                title_color: '#5F0487',
                template: '',
                show_title: true,
                sections: []
            },
            categories: [
                'AÇÃO SOCIAL E COMUNIDADE',
                'ACESSIBILIDADE E INCLUSÃO',
                'ALIMENTAÇÃO E COMBATE À FOME',
                'APOIO A IDOSOS / ASILOS',
                'APOIO A REFUGIADOS',
                'CAUSA ANIMAL',
                'CAUSA LGBTQIAPN+',
                'COMBATE AO CÂNCER',
                'CULTURA E ARTE',
                'DEFESA DOS DIREITOS HUMANOS',
                'EDUCAÇÃO E ENSINO',
                'EMERGÊNCIA E DESASTRES NATURAIS',
                'EMPREENDEDORISMO E NEGÓCIOS',
                'ESPORTE E COMPETIÇÕES',
                'EVENTOS E CELEBRAÇÕES',
                'GAMES E JOGOS DE TABULEIRO',
                'JORNALISMO E MÍDIA INDEPENDENTE',
                'LITERATURA E PUBLICAÇÕES',
                'MEIO AMBIENTE E SUSTENTABILIDADE',
                'MÚSICA E GRAVAÇÕES',
                'ONGs E INSTITUIÇÕES',
                'PESQUISA CIENTÍFICA',
                'PROJETOS AUDIOVISUAIS / CINEMA',
                'PROJETOS TECNOLÓGICOS',
                'PROTEÇÃO À INFÂNCIA',
                'REFORMA E CONSTRUÇÃO',
                'RESGATE E REABILITAÇÃO',
                'SAÚDE MENTAL E BEM-ESTAR',
                'TRATAMENTO MÉDICO / ENFERMIDADE',
                'VIAGENS E INTERCÂMBIO',
                'OUTRAS CAUSAS'
            ],

            presets: {
                layout_1: [
                    {
                        type: 'text',
                        order: 0,
                        content: {
                            title: 'A Solução Definitiva para Alavancar o Seu Negócio',
                            text: '<div class="py-2"><span class="badge rounded-pill px-3 py-2 mb-3 fw-bold" style="background-color: rgba(95, 4, 135, 0.1); color: #5F0487;">🚀 NOVIDADE &amp; TECNOLOGIA</span><h2 class="fw-extrabold text-dark mb-3" style="letter-spacing: -0.5px;">Conecte sua Marca ao Futuro da Performance</h2><p class="fs-6 text-secondary leading-relaxed">Aumente a produtividade da sua equipe com uma plataforma intuitiva, ágil e desenvolvida para gerar resultados imediatos e mensuráveis.</p></div>',
                            bg_color: '#ffffff',
                            accent_color: '#5F0487',
                            show_title: true,
                            show_text: true
                        }
                    },
                    {
                        type: 'youtube',
                        order: 1,
                        content: {
                            title: 'Veja como Funciona na Prática (Demonstração)',
                            url: 'https://www.youtube.com/watch?v=cvnzkIZjwno',
                            bg_color: '#f8fafc',
                            accent_color: '#5F0487',
                            show_title: true,
                            show_url: true
                        }
                    },
                    {
                        type: 'carousel',
                        order: 2,
                        content: {
                            title: 'Nossa Galeria e Recursos em Destaque',
                            images: [
                                { url: '', id: null },
                                { url: '', id: null }
                            ],
                            bg_color: '#ffffff',
                            accent_color: '#5F0487',
                            show_title: true
                        }
                    },
                    {
                        type: 'payment',
                        order: 3,
                        content: {
                            button_text: 'Garantir Acesso Imediato',
                            amount: 197.00,
                            bg_color: '#f8fafc',
                            accent_color: '#5F0487',
                            show_button_text: true
                        }
                    },
                    {
                        type: 'contact',
                        order: 4,
                        content: {
                            title: 'Fale com Nossos Especialistas',
                            whatsapp: '(11) 99999-9999',
                            email: 'contato@rmrsistemas.com.br',
                            bg_color: '#ffffff',
                            accent_color: '#5F0487',
                            show_title: true,
                            show_whatsapp: true,
                            show_email: true
                        }
                    }
                ],

                // 2. LAYOUT CLEAN (Minimalista, Profissional, para Consultorias, Agências ou Serviços)
                layout_2: [
                    {
                        type: 'text',
                        order: 0,
                        content: {
                            title: 'Consultoria & Estratégia Sob Medida',
                            text: '<div class="py-1"><h3 class="fw-bold text-dark mb-2">Simplicidade e Foco no que Realmente Gera Valor</h3><p class="fs-6 text-muted mb-3">Ajudamos sua empresa a escalar com processos inteligentes, gestão transparente e automação de alta fidelidade.</p><div class="d-flex gap-2 flex-wrap mb-2"><span class="badge bg-light text-dark border px-3 py-2 rounded-pill"><i class="bi bi-check2-circle text-success me-1"></i> Processos Ágeis</span><span class="badge bg-light text-dark border px-3 py-2 rounded-pill"><i class="bi bi-check2-circle text-success me-1"></i> Suporte Dedicado</span><span class="badge bg-light text-dark border px-3 py-2 rounded-pill"><i class="bi bi-check2-circle text-success me-1"></i> Resultados Reais</span></div></div>',
                            bg_color: '#ffffff',
                            accent_color: '#1e293b',
                            show_title: true,
                            show_text: true
                        }
                    },
                    {
                        type: 'carousel',
                        order: 1,
                        content: {
                            title: 'Portfólio de Projetos e Casos de Sucesso',
                            images: [
                                { url: '', id: null }
                            ],
                            bg_color: '#f8fafc',
                            accent_color: '#1e293b',
                            show_title: true
                        }
                    },
                    {
                        type: 'contact',
                        order: 2,
                        content: {
                            title: 'Agende uma Reunião ou Solicite um Orçamento',
                            whatsapp: '(11) 98888-8888',
                            email: 'atendimento@rmrsistemas.com.br',
                            bg_color: '#ffffff',
                            accent_color: '#1e293b',
                            show_title: true,
                            show_whatsapp: true,
                            show_email: true
                        }
                    }
                ],
                layout_3: [
                    {
                        type: 'dailypag_crowdfunding',
                        order: 0,
                        content: {
                            title: 'Focinhos com Fome',
                            category: 'CAUSA ANIMAL',
                            location: 'BRASIL',
                            cover_image: '',
                            short_description: 'Todos os dias, centenas de cães e gatos de rua sofrem com a fome e o frio. Nossa missão é distribuir ração e cuidar desses anjos.',
                            pix_key: 'campanha@dailypag.com.br',
                            about_text: '<p class="fw-bold text-dark">Nossa Missão:</p><p>Acreditamos que todo animal merece uma barriga cheia e um lugar quente para dormir.</p>',
                            raised_amount: 0.00,
                            goal_amount: 5000.00,
                            supporters_count: 0,
                            creator_name: 'Equipe Proteção Animal',
                            amount: 25.00,
                            allows_fractional: true,

                            // Controles de visibilidade ativados por padrão
                            show_title: true,
                            show_category: true,
                            show_location: true,
                            show_cover_image: true,
                            show_raised_amount: true,
                            show_goal: true,
                            show_supporters: true,
                            show_short_description: true,
                            show_pix_key: true,
                            show_about_text: true,
                            show_creator: true,
                            show_share_button: true
                        }
                    }
                ]
            }
        };
    },
    watch: {
        'form.template'(newVal) {
            if (newVal && !this.isEditing) {
                this.loadPreset(newVal);
            }
        }
    },
    async created() {
        const id = this.$route.params.id;
        if (id) {
            this.isEditing = true;
            this.pageId = id;
            await this.fetchPageData(id);
        } else {
            this.form.sections = [];
        }
    },
    methods: {
        handleTemplateChange(event) {
            const selectedKey = event ? event.target.value : this.form.template;
            this.form.template = selectedKey;
            if (selectedKey && this.presets && this.presets[selectedKey]) {
                this.form.sections = JSON.parse(JSON.stringify(this.presets[selectedKey]));
            }
            else {
                this.form.sections = [];
                this.form.template = '';

                if (!this.isEditing) {
                    this.form.title = 'Nova Página';
                    this.form.title_color = '#5F0487';
                    this.form.show_title = true;
                }
            }
        },

        loadPreset(templateKey) {
            if (this.presets[templateKey]) {
                this.form.sections = JSON.parse(JSON.stringify(this.presets[templateKey]));
            }
        },
        async fetchPageData(id) {
            this.loading = true;
            try {
                const response = await api.get(`/client-jwt/landing-pages/${id}`);
                const pageData = response.data.data || response.data;

                this.form.title = pageData.title;
                this.form.title_color = pageData.title_color || '#5F0487';
                this.form.template = pageData.template || '';
                this.form.sections = pageData.content || [];
            } catch (error) {
                console.error('Erro ao carregar dados da página:', error);
                this.showAlert('Não foi possível carregar os dados da página.', 'danger');
            } finally {
                this.loading = false;
            }
        },


        addSection(type) {
            let defaultContent = { title: '', bg_color: '#ffffff', accent_color: '#5F0487' };

            if (type === 'dailypag_crowdfunding') {
                // Aqui está a correção: Uso do encadeamento opcional (?.) para não quebrar a tela
                const preset = this.presets?.layout_3?.[0]?.content;
                if (preset) {
                    defaultContent = JSON.parse(JSON.stringify(preset));
                }
            }
            else if (type === 'text') defaultContent = { ...defaultContent, text: '<p>Insira seu texto aqui...</p>', show_title: true, show_text: true };
            else if (type === 'youtube') defaultContent = { ...defaultContent, url: '', show_title: true, show_url: true };
            else if (type === 'contact') defaultContent = { ...defaultContent, title: 'Fale Conosco', whatsapp: '', email: '', show_title: true, show_whatsapp: true, show_email: true };
            else if (type === 'payment') defaultContent = { ...defaultContent, button_text: 'Garantir Acesso', amount: 50.00, button_action: 'checkout', button_link: '', show_button_text: true };
            else if (type === 'carousel') defaultContent = { ...defaultContent, title: 'Nossa Galeria', images: [{ url: '' }], show_title: true };

            this.form.sections.push({ type, content: defaultContent });
        },

        removeSection(index) {
            this.form.sections.splice(index, 1);
        },

        moveSection(index, direction) {
            const newIndex = index + direction;
            if (newIndex >= 0 && newIndex < this.form.sections.length) {
                const temp = this.form.sections[index];
                this.form.sections[index] = this.form.sections[newIndex];
                this.form.sections[newIndex] = temp;
            }
        },

        handleCroppedBlob(blob, targetObj, property) {
            const file = new File([blob], 'imagem_recortada.jpg', { type: 'image/jpeg' });

            // Garante que se o objeto já tem uma chave, nós usamos ela. 
            // Caso contrário, criamos uma nova chave única exclusiva para ESTE item.
            if (!targetObj.temp_file_key) {
                targetObj.temp_file_key = 'crop_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
            }

            const fileKey = targetObj.temp_file_key;

            // Define o preview local apenas no elemento alvo (banner ou item específico do carrossel)
            targetObj[property] = URL.createObjectURL(file);

            // Atualiza ou adiciona o arquivo correspondente na fila global de pendentes
            const existingIndex = this.pendingFiles.findIndex(pf => pf.key === fileKey);
            if (existingIndex !== -1) {
                this.pendingFiles[existingIndex].file = file;
            } else {
                this.pendingFiles.push({ key: fileKey, file: file });
            }
        },

        removeCarouselImage(section, index) {
            const removedImage = section.content.images[index];
            if (removedImage && removedImage.temp_file_key) {
                this.pendingFiles = this.pendingFiles.filter(pf => pf.key !== removedImage.temp_file_key);
            }
            section.content.images.splice(index, 1);
        },

        handleCurrencyInput(e, obj, key) {
            let val = e.target.value.replace(/\D/g, '');
            let numeric = parseInt(val || '0', 10) / 100;
            obj[key] = numeric;
            e.target.value = numeric.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatCurrency(v) {
            return parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        openCheckoutModal(content) {
            this.activeCheckoutContent = content;
            this.checkoutAmountRaw = content.amount || 0;
            this.displayCheckoutAmount = this.formatCurrency(this.checkoutAmountRaw);
            this.checkoutModalOpen = true;
        },

        closeCheckoutModal() {
            this.checkoutModalOpen = false;
        },

        handleCheckoutMask(e) {
            let val = e.target.value.replace(/\D/g, '');
            this.checkoutAmountRaw = parseInt(val || '0', 10) / 100;
            this.displayCheckoutAmount = this.formatCurrency(this.checkoutAmountRaw);
        },

        simulatePayment() {
            this.showAlert('Sucesso! Redirecionando para o pagamento real...', 'success');
            this.closeCheckoutModal();
        },

        openFullPreviewModal() { this.fullPreviewModalOpen = true; },
        closeFullPreviewModal() { this.fullPreviewModalOpen = false; },
        openAiModal() { this.showAiModal = true; },
        closeAiModal() { this.showAiModal = false; },

        async savePage() {
            this.loading = true;

            try {
                const formData = new FormData();
                formData.append('title', this.form.title);
                formData.append('title_color', this.form.title_color);
                formData.append('content', JSON.stringify(this.form.sections));
                formData.append('is_active', 1);

                this.pendingFiles.forEach(pf => {
                    formData.append(`images[${pf.key}]`, pf.file);
                });

                if (this.isEditing) {
                    formData.append('_method', 'PUT');
                    await api.post(`/client-jwt/landing-pages/${this.pageId}`, formData);
                    this.showAlert('Página atualizada com sucesso!');

                    // A MÁGICA AQUI: Recarrega os dados fresquinhos do backend para limpar os 'blob:' da tela
                    await this.fetchPageData(this.pageId);

                } else {
                    const response = await api.post('/client-jwt/landing-pages', formData);
                    this.showAlert('Página salva com sucesso!');

                    if (response.data && response.data.id) {
                        this.pageId = response.data.id;
                        this.isEditing = true;

                        // A MÁGICA AQUI: Recarrega os dados e renderiza as URLs reais do servidor
                        await this.fetchPageData(this.pageId);

                        this.$router.replace(`/edit-landing-page/${this.pageId}`);
                    }
                }

                // Limpa a fila de pendentes corretamente
                this.pendingFiles = [];

            } catch (error) {
                console.error(error);
                this.showAlert('Erro ao salvar a página. Verifique os dados.', 'danger');
            } finally {
                this.loading = false;
            }
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            this.alerts.push({ id, message, type, icon: 'bi-check-circle-fill' });
            setTimeout(() => { this.alerts = this.alerts.filter(a => a.id !== id); }, 4000);
        },
        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        formatText(command) {
            document.execCommand(command, false, null);
        },
        validateMinAmount(content) {
            if (!content.amount || content.amount < 10) {
                content.amount = 10;
                this.showAlert('O valor sugerido mínimo para doação é de R$ 10,00.', 'warning');
            }
        },
        async generateWithAi() {
    // Validação local rápida para evitar requisição desnecessária
    if (!this.aiObjective || this.aiObjective.trim().length < 5) {
        this.showAlert('Por favor, detalhe um pouco mais o objetivo (mínimo de 5 caracteres).', 'warning');
        return;
    }

    this.aiLoading = true;
    
    try {
        // Chamada real para a nossa nova rota do Laravel
        const response = await api.post('/client-jwt/landing-pages/suggest-donation', {
        objective: this.aiObjective,
        template: this.form.template || 'layout_3' 
    });

        const generatedData = response.data.data;

        // Injeta o conteúdo gerado pela IA direto no formulário da página
        this.form.title = generatedData.title;
        this.form.sections = generatedData.sections;
        
        // Se você tiver um controle de templates, já pode forçar o layout ideal
        // this.form.template = 'layout_3'; 

        this.showAlert('Campanha solidária gerada com sucesso!', 'success');
        this.closeAiModal(); // Fecha o modal após o sucesso
        this.aiObjective = ''; // Limpa o campo

    } catch (error) {
        console.error("Erro na IA:", error);

        // Tratamento do erro 422 (Validação do FormRequest do Laravel)
        if (error.response && error.response.status === 422) {
            const errors = error.response.data.errors;
            if (errors && errors.objective) {
                // Exibe a mensagem de validação exata que criamos no Laravel
                this.showAlert(errors.objective[0], 'danger');
                return;
            }
        }
        
        // Fallback genérico para outros erros
        const errorMessage = error.response?.data?.message || 'Erro ao gerar campanha com a IA.';
        this.showAlert(errorMessage, 'danger');

    } finally {
        this.aiLoading = false;
    }
}
    }
}
</script>

<style scoped>
.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #5F0487;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.modal-browser-overlay,
.ai-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(15, 23, 42, 0.75);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    backdrop-filter: blur(5px);
}

.modal-browser-container {
    width: 100%;
    max-width: 1200px;
    height: 90vh;
    background: #ffffff;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.ai-modal-content {
    width: 100%;
    max-width: 500px;
}

.payment-btn {
    background-color: #ffffff;
    border: 2px solid #e2e8f0;
    transition: all 0.2s ease-in-out;
    cursor: pointer;
}

.payment-btn:hover {
    border-color: #5F0487;
    background-color: #f8fafc;
    transform: translateY(-2px);
}

.payment-btn.border-primary {
    border-color: #0d6efd !important;
}

.payment-btn.border-info {
    border-color: #0dcaf0 !important;
}

.payment-btn.border-success {
    border-color: #198754 !important;
}

.card-preview-container {
    perspective: 1000px;
    width: 100%;
    max-width: 320px;
    height: 195px;
    margin: 0 auto;
}

.card-inner {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.8s;
    transform-style: preserve-3d;
}

.card-inner.is-flipped {
    transform: rotateY(180deg);
}

.card-front,
.card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    border-radius: 16px;
    padding: 20px;
    color: white;
}

.card-front {
    background: linear-gradient(135deg, #5F0487 0%, #101930 100%);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.card-back {
    background: linear-gradient(135deg, #1a202c 0%, #000000 100%);
    transform: rotateY(180deg);
    padding: 0;
}

.chip {
    width: 42px;
    height: 32px;
    background: linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%);
    border-radius: 6px;
}

.magnetic-strip {
    width: 100%;
    height: 40px;
    background: #000;
}

.card-number-display {
    font-family: 'Courier New', monospace;
    letter-spacing: 2px;
}

.btn-rmr-primary {
    background-color: #5F0487;
    color: white;
}

.btn-rmr-primary:hover {
    background-color: #4a036a;
}

.form-label-modern {
    font-size: 0.825rem;
    font-weight: 600;
    color: #475569;
    letter-spacing: 0.3px;
    margin-bottom: 0.4rem;
}

.form-control-pmg-modern,
.form-select-pmg-modern {
    padding: 0.7rem 1rem !important;
    font-size: 0.925rem !important;
    border-radius: 12px !important;
    border: 1px solid #e2e8f0 !important;
    background-color: #ffffff;
    color: #1e293b;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.form-control-pmg-modern:focus,
.form-select-pmg-modern:focus {
    border-color: #5F0487 !important;
    box-shadow: 0 0 0 4px rgba(95, 4, 135, 0.12) !important;
    background-color: #ffffff;
    outline: none;
}

.form-control-pmg-modern:disabled,
.form-control-pmg-modern[disabled],
.form-select-pmg-modern:disabled {
    background-color: #f8fafc !important;
    border-color: #e2e8f0 !important;
    color: #64748b !important;
    cursor: not-allowed;
    opacity: 0.85;
}

.form-control-pmg-modern[type="file"]::file-selector-button {
    background-color: #f1f5f9;
    color: #475569;
    border: none;
    padding: 0.4rem 0.8rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.8rem;
    margin-right: 0.8rem;
    transition: all 0.2s ease;
}

.form-control-pmg-modern[type="file"]::file-selector-button:hover {
    background-color: #e2e8f0;
    color: #0f172a;
}

.btn-eye-toggle {
    padding: 2px 6px;
    border-radius: 6px;
    transition: background 0.2s;
}

.btn-eye-toggle:hover {
    background-color: #f1f5f9;
}

.editor-wrapper {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    background-color: #ffffff;
    transition: all 0.2s ease-in-out;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.editor-wrapper:focus-within {
    border-color: #5F0487 !important;
    box-shadow: 0 0 0 4px rgba(95, 4, 135, 0.12) !important;
}

.editor-toolbar {
    background-color: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 6px 10px;
    display: flex;
    align-items: center;
    gap: 4px;
}

.btn-editor-action {
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    color: #475569;
    padding: 4px 10px;
    font-size: 0.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
}

.btn-editor-action:hover {
    background-color: #e2e8f0;
    color: #0f172a;
}

.editor-content {
    padding: 0.8rem 1rem;
    font-size: 0.925rem;
    color: #1e293b;
    outline: none;
    line-height: 1.5;
}
.alerts-container,
.toast-container {
    position: fixed !important;
    top: 20px;
    right: 20px;
    z-index: 99999 !important;
    pointer-events: none; 
}

.alert {
    pointer-events: auto;
}
</style>